import React, { useState } from 'react';
import { Modal } from '../../../components/common/Modal';
import { type DashboardCardKey } from '../../../features/dashboard/preferences/constants';
import {
  DASHBOARD_MENU_GROUPS,
  DASHBOARD_MENU_SINGLE_ITEMS,
  DASHBOARD_MENU_ITEM_LABELS,
} from '../constants';
import { useLanguage } from '../../../features/language/useLanguage';

const Toggle = ({ checked, onChange, id }: { checked: boolean, onChange: (v: boolean) => void, id: string }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`${
      checked ? 'bg-primary-600' : 'bg-gray-200'
    } relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none`}
    data-test-id={id}
  >
    <span
      className={`${
        checked ? 'translate-x-4' : 'translate-x-0'
      } pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
    />
  </button>
);

interface DashboardMenuModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialHiddenCards: string[];
  onSave: (hiddenCards: string[]) => void;
}

export const DashboardMenuModal: React.FC<DashboardMenuModalProps> = ({
  isOpen,
  onClose,
  initialHiddenCards,
  onSave,
}) => {
  const { t } = useLanguage();
  const [draftHiddenCards, setDraftHiddenCards] = useState<string[]>(initialHiddenCards);
  const groupLabelMap: Record<string, string> = {
    'financial-stats': t('dashboard.menu.groupFinancialStats'),
    'green-bean-profile': t('dashboard.menu.groupGreenBeanProfile'),
  };
  const singleItemLabelMap: Record<string, string> = {
    activity_feed: t('dashboard.menu.itemActivityFeed'),
    inventory_expiring_soon: t('dashboard.menu.itemExpiringSoon'),
    inventory_low_stock: t('dashboard.menu.itemLowStock'),
    average_shrinkage: t('dashboard.menu.itemAverageShrinkage'),
    quick_actions: t('dashboard.menu.itemQuickActions'),
  };
  const cardLabelMap: Record<string, string> = {
    financial_revenue: t('dashboard.menu.cardRevenue'),
    financial_expenses: t('dashboard.menu.cardExpenses'),
    financial_net_profit: t('dashboard.menu.cardNetProfit'),
    financial_outstanding: t('dashboard.menu.cardOutstandingPayment'),
    financial_green_bean: t('dashboard.menu.cardGreenBean'),
    financial_roasted: t('dashboard.menu.cardRoasted'),
    financial_finished: t('dashboard.menu.cardFinished'),
    green_bean_species: t('dashboard.menu.cardSpecies'),
    green_bean_varietals: t('dashboard.menu.cardVarietals'),
  };

  const isDraftCardVisible = (key: DashboardCardKey) => !draftHiddenCards.includes(key);

  const toggleDraftVisibility = (key: DashboardCardKey, visible: boolean) => {
    setDraftHiddenCards((prev) => 
      visible ? prev.filter((entry) => entry !== key) : [...new Set([...prev, key])]
    );
  };

  const toggleDraftGroup = (keys: DashboardCardKey[], visible: boolean) => {
    setDraftHiddenCards((prev) => {
      const next = new Set(prev);
      keys.forEach((key) => visible ? next.delete(key) : next.add(key));
      return Array.from(next);
    });
  };

  const handleClose = () => {
    setDraftHiddenCards(initialHiddenCards);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('dashboard.menu.customizeTitle')}
      maxWidth="3xl"
    >
      <div className="flex flex-col h-full">
        <div className="mb-6">
          <p className="text-sm text-gray-500">
            {t('dashboard.menu.description')}
          </p>
        </div>

        <div 
          className="grid grid-cols-1 md:grid-cols-2 gap-8"
          data-test-id="dashboard-menu-modal-content"
        >
          <div className="space-y-6">
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 px-1">
              {t('dashboard.menu.mainSections')}
            </h3>
            {DASHBOARD_MENU_GROUPS.map((group) => {
              const checkedCount = group.childKeys.filter(isDraftCardVisible).length;
              const isGroupChecked = checkedCount > 0;

              return (
                <div key={group.id} className="group overflow-hidden rounded-2xl border border-gray-100 bg-white transition-all hover:border-primary-100 hover:shadow-sm">
                  <div className="flex items-center justify-between bg-gray-50/50 p-4 transition-colors group-hover:bg-primary-50/30">
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold text-gray-900">{groupLabelMap[group.id] ?? group.label}</span>
                      <span className="text-xs text-gray-500">
                        {t('dashboard.menu.activeCount', { count: checkedCount, total: group.childKeys.length })}
                      </span>
                    </div>
                    <Toggle 
                      id={`group-${group.id}`}
                      checked={isGroupChecked} 
                      onChange={(val) => toggleDraftGroup(group.childKeys, val)} 
                    />
                  </div>
                  <div className="divide-y divide-gray-50 px-2 py-1">
                    {group.childKeys.map((key) => (
                      <div key={key} className="flex items-center justify-between px-3 py-3 transition-colors hover:bg-gray-50/50 rounded-lg">
                        <span className="text-sm text-gray-600">
                          {cardLabelMap[key] ?? DASHBOARD_MENU_ITEM_LABELS[key] ?? key}
                        </span>
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 cursor-pointer"
                          style={{ accentColor: '#0277BD' }}
                          checked={isDraftCardVisible(key)}
                          onChange={(e) => toggleDraftVisibility(key, e.target.checked)}
                          data-test-id={`dashboard-menu-item-${key}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="space-y-6">
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 px-1">
              {t('dashboard.menu.quickStats')}
            </h3>
            <div className="space-y-3">
              {DASHBOARD_MENU_SINGLE_ITEMS.map((item) => (
                <div
                  key={item.key}
                  className="flex items-center justify-between p-4 rounded-2xl border border-gray-100 bg-white transition-all hover:border-primary-100 hover:shadow-md"
                >
                  <span className="text-sm font-medium text-gray-700">{singleItemLabelMap[item.key] ?? item.label}</span>
                  <Toggle 
                    id={`item-${item.key}`}
                    checked={isDraftCardVisible(item.key)} 
                    onChange={(val) => toggleDraftVisibility(item.key, val)} 
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-10 flex items-center justify-end gap-3 pt-6 border-t border-gray-100">
          <button
            type="button"
            onClick={handleClose}
            className="rounded-xl px-5 py-2.5 text-sm font-semibold text-gray-600 transition-all hover:bg-gray-100"
            data-test-id="dashboard-menu-cancel-button"
          >
            {t('dashboard.menu.cancel')}
          </button>

          <button
            type="button"
            onClick={() => onSave(draftHiddenCards)}
            className="rounded-xl bg-primary-600 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-gray-200 transition-all hover:bg-primary-800 hover:scale-[1.02] active:scale-[0.98]"
            data-test-id="dashboard-menu-save-button"
          >
            {t('dashboard.menu.applyChanges')}
          </button>
        </div>
      </div>
    </Modal>
  );
};
